 
import ActivityMediaType from 'components/Activity/ActivityMediaType';
import ApplicationSubjectItem from 'components/Application/ApplicationSubjectItem';
import SortIcon from 'components/SortIcon';
import TaskPriorityIcon from 'components/Task/TaskPriorityIcon';
import { TaskMediaType } from 'core/enums/taskMediaType';
import TaskStatus from 'core/enums/taskStatus';
import Task from 'core/models/task';
import { getContactLabel } from 'core/useCases/contacts/utils';
import { renderTaskMediaText } from 'core/useCases/tasks/utils';
import useResponsive from 'hooks/responsive';
import Checked from 'images/icons/Checked.svg?react';
import _ from 'lodash';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SBoldPrimaryText, STable } from 'styles/components';
import { computeDateWithoutTime, getCompleteDate, getNumericDate } from 'utils/dateTimeFormat';
import MarkTaskAsCompleteButton from '../MarkTaskAsCompleteButton';
import TaskActions from '../TaskActions';
import {
    SCard,
    SCardHeader,
    SCardHeaderLeft,
    SCardLine,
    SCardLineLeft,
    SCardLineRight,
    SSubject,
    STwoLines,
} from './styles';

type SortColumn = 'subject' | 'deadline';

type SortState = {
    column: SortColumn;
    direction: 'asc' | 'desc';
};

const DEFAULT_SORT: SortState = { column: 'subject', direction: 'asc' };

type TasksByDate = {
    tasks: Task[];
    date: Date;
    deadlineKey: string;
};

type TaskListProps = {
    tasks: Task[];
};

const TaskList: FC<TaskListProps> = ({ tasks }) => {
    const { t } = useTranslation();
    const [sortByDay, setSortByDay] = useState<Record<string, SortState>>({});
    const { isMobile } = useResponsive();

    const getSortForDay = (deadlineKey: string): SortState => sortByDay[deadlineKey] ?? DEFAULT_SORT;

    const tasksByDateList = useMemo(
        () =>
            _(tasks)
                // `deadline` est un timestamp (avec heure) : grouper sur sa valeur brute
                // créerait un tableau par tâche. On regroupe par jour calendaire, comme
                // EventList (cf. computeDateWithoutTime), pour avoir un tableau par jour.
                .groupBy((task: Task) => String(computeDateWithoutTime(new Date(task.deadline))))
                .map((items, deadlineKey) => {
                    const sort = sortByDay[deadlineKey] ?? DEFAULT_SORT;
                    const subjectIteratee = (task: Task) =>
                        (task.customSubject || t(`common.task-subject.enum.${task.subject}`)).toLowerCase();
                    const deadlineIteratee = (task: Task) => new Date(task.initialDeadline).getTime();
                    const iteratees =
                        sort.column === 'subject'
                            ? [subjectIteratee, deadlineIteratee]
                            : [deadlineIteratee, subjectIteratee];

                    return {
                        date: new Date(deadlineKey),
                        deadlineKey,
                        tasks: _.orderBy(items, iteratees, [sort.direction, 'asc']),
                    };
                })
                .value(),
        [tasks, sortByDay, t],
    );

    const toggleSortForDay = (deadlineKey: string, column: SortColumn) => {
        setSortByDay((prev) => {
            const current = prev[deadlineKey] ?? DEFAULT_SORT;
            if (current.column === column) {
                return {
                    ...prev,
                    [deadlineKey]: { column, direction: current.direction === 'asc' ? 'desc' : 'asc' },
                };
            }
            return { ...prev, [deadlineKey]: { column, direction: 'asc' } };
        });
    };

    return (
        <div>
            {tasksByDateList?.map((tasksByDate: TasksByDate) => {
                const sort = getSortForDay(tasksByDate.deadlineKey);
                // Direction active par colonne (`null` = triable mais non triée) :
                // pilote l'icône de tri (chevrons haut/bas) sur Sujet et Échéance.
                const subjectDirection = sort.column === 'subject' ? sort.direction : null;
                const deadlineDirection = sort.column === 'deadline' ? sort.direction : null;

                return isMobile ? (
                    <div key={String(tasksByDate.date)}>
                        <h2>{getCompleteDate(tasksByDate.date, t)}</h2>
                        {tasksByDate.tasks.map((task: Task, index: number) => {
                            const {
                                id,
                                subject,
                                customSubject,
                                mediaType,
                                contact,
                                initialDeadline,
                                priority,
                                status,
                                application,
                            } = task;

                            return (
                                <SCard key={id} isEven={index % 2 === 0}>
                                    <SCardHeader>
                                        <SCardHeaderLeft>
                                            {TaskStatus.NotStarted === status && (
                                                <MarkTaskAsCompleteButton taskId={id} />
                                            )}
                                            {TaskStatus.Completed === status && <Checked />}
                                            {!customSubject ? (
                                                <Link
                                                    title={t(`common.task-subject.enum.${subject}`)}
                                                    to={`/task/${id}`}
                                                >
                                                    <SSubject isCompleted={TaskStatus.Completed === status}>
                                                        {t(`common.task-subject.enum.${subject}`)}
                                                    </SSubject>
                                                </Link>
                                            ) : (
                                                <Link title={customSubject} to={`/task/${id}`}>
                                                    <SSubject isCompleted={TaskStatus.Completed === status}>
                                                        {customSubject}
                                                    </SSubject>
                                                </Link>
                                            )}
                                        </SCardHeaderLeft>
                                        <TaskActions taskId={id} />
                                    </SCardHeader>
                                    <SCardLine>
                                        <SCardLineLeft>{t('application.label')}</SCardLineLeft>
                                        <SCardLineRight>
                                            <ApplicationSubjectItem application={application} />
                                        </SCardLineRight>
                                    </SCardLine>
                                    <SCardLine>
                                        <SCardLineLeft>{t('common.media-type.label')}</SCardLineLeft>
                                        <SCardLineRight>
                                            {contact && mediaType ? (
                                                <STwoLines>
                                                    <ActivityMediaType mediaType={mediaType} />
                                                    {renderTaskMediaText(mediaType, contact)}
                                                </STwoLines>
                                            ) : (
                                                <STwoLines>
                                                    <ActivityMediaType mediaType={TaskMediaType.Other} />
                                                    {application?.sourceDetails}
                                                </STwoLines>
                                            )}
                                        </SCardLineRight>
                                    </SCardLine>
                                    <SCardLine>
                                        <SCardLineLeft>{t('common.contact')}</SCardLineLeft>
                                        <SCardLineRight>
                                            {contact && (
                                                <>
                                                    <Link to={`/contact/${contact.id}`}>
                                                        <SBoldPrimaryText>
                                                            {getContactLabel(contact, t)}
                                                        </SBoldPrimaryText>
                                                    </Link>
                                                    <br />
                                                    <span>{contact.company}</span>
                                                </>
                                            )}
                                        </SCardLineRight>
                                    </SCardLine>
                                    <SCardLine>
                                        <SCardLineLeft>{t('common.intermediary.label')}</SCardLineLeft>
                                        <SCardLineRight>
                                            {contact && (
                                                <div>
                                                    {contact.parent ? (
                                                        <Link to={`/contact/${contact.parent.id}`}>
                                                            {getContactLabel(contact.parent, t)}
                                                        </Link>
                                                    ) : (
                                                        <span>{t('common.you')}</span>
                                                    )}
                                                </div>
                                            )}
                                        </SCardLineRight>
                                    </SCardLine>
                                    <SCardLine>
                                        <SCardLineLeft>
                                            <button
                                                onClick={() =>
                                                    toggleSortForDay(tasksByDate.deadlineKey, 'deadline')
                                                }
                                                type="button"
                                            >
                                                {t('common.end-date')}
                                                <SortIcon direction={deadlineDirection} />
                                            </button>
                                        </SCardLineLeft>
                                        <SCardLineRight>{getNumericDate(initialDeadline)}</SCardLineRight>
                                    </SCardLine>
                                    <SCardLine>
                                        <SCardLineLeft>{t('common.priority')}</SCardLineLeft>
                                        <SCardLineRight>
                                            <TaskPriorityIcon priority={priority} />
                                        </SCardLineRight>
                                    </SCardLine>
                                </SCard>
                            );
                        })}
                    </div>
                ) : (
                    <div key={String(tasksByDate.date)}>
                        <h2>{getCompleteDate(tasksByDate.date, t)}</h2>
                        <STable>
                            <thead>
                                <tr>
                                    { }
                                    <th />
                                    <th>
                                        <button
                                            onClick={() => toggleSortForDay(tasksByDate.deadlineKey, 'subject')}
                                            type="button"
                                        >
                                            {t('common.subject.label')}
                                            <SortIcon direction={subjectDirection} />
                                        </button>
                                    </th>
                                    <th>{t('application.label')}</th>
                                    <th>{t('common.media-type.label')}</th>
                                    <th>{t('common.contact')}</th>
                                    <th>{t('common.intermediary.label')}</th>
                                    <th>
                                        <button
                                            onClick={() => toggleSortForDay(tasksByDate.deadlineKey, 'deadline')}
                                            type="button"
                                        >
                                            {t('common.end-date')}
                                            <SortIcon direction={deadlineDirection} />
                                        </button>
                                    </th>
                                    <th>{t('common.priority')}</th>
                                    <th aria-label={t('common.action')} />
                                </tr>
                            </thead>
                            <tbody className="table--task">
                                {tasksByDate.tasks.map((task: Task) => {
                                    const {
                                        id,
                                        subject,
                                        customSubject,
                                        mediaType,
                                        contact,
                                        initialDeadline,
                                        priority,
                                        status,
                                        application,
                                    } = task;

                                    return (
                                        <tr key={id}>
                                            <td>
                                                {TaskStatus.NotStarted === status && (
                                                    <MarkTaskAsCompleteButton taskId={id} />
                                                )}
                                                {TaskStatus.Completed === status && <Checked />}
                                            </td>
                                            <td>
                                                {!customSubject ? (
                                                    <Link
                                                        title={t(`common.task-subject.enum.${subject}`)}
                                                        to={`/task/${id}`}
                                                    >
                                                        <SSubject isCompleted={TaskStatus.Completed === status}>
                                                            {t(`common.task-subject.enum.${subject}`)}
                                                        </SSubject>
                                                    </Link>
                                                ) : (
                                                    <Link title={customSubject} to={`/task/${id}`}>
                                                        <SSubject isCompleted={TaskStatus.Completed === status}>
                                                            {customSubject}
                                                        </SSubject>
                                                    </Link>
                                                )}
                                            </td>
                                            <td>
                                                <ApplicationSubjectItem application={application} />
                                            </td>
                                            <td>
                                                {contact && mediaType ? (
                                                    <>
                                                        <ActivityMediaType mediaType={mediaType} />
                                                        {renderTaskMediaText(mediaType, contact)}
                                                    </>
                                                ) : (
                                                    <>
                                                        <ActivityMediaType mediaType={TaskMediaType.Other} />
                                                        {application?.sourceDetails}
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                {contact && (
                                                    <>
                                                        <Link to={`/contact/${contact.id}`}>
                                                            <SBoldPrimaryText>
                                                                {getContactLabel(contact, t)}
                                                            </SBoldPrimaryText>
                                                        </Link>
                                                        <br />
                                                        <span>{contact.company}</span>
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                {contact && (
                                                    <div>
                                                        {contact.parent ? (
                                                            <Link to={`/contact/${contact.parent.id}`}>
                                                                {getContactLabel(contact.parent, t)}
                                                            </Link>
                                                        ) : (
                                                            <span>{t('common.you')}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{getNumericDate(initialDeadline)}</td>
                                            <td>
                                                <TaskPriorityIcon priority={priority} />
                                            </td>
                                            <td>
                                                <div className="showOnRowHover">
                                                    <TaskActions taskId={id} />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </STable>
                    </div>
                );
            })}
        </div>
    );
};

export default TaskList;
